import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  contrastRatio,
  REPORT_ACCENT_MIN_CONTRAST,
  REPORT_LOGO_MIMES,
  type PresignResponse,
  type ReportStyleDto,
} from "@klantpunt/shared";
import { IconTrash, IconUpload } from "../../app/icons";
import { api, ApiError } from "../../lib/api";
import { photoUrl } from "../../lib/photos";
import { useToast } from "../../lib/toast";
import { putToStorage } from "../../lib/upload";

/** Klantpunt-paars: de standaard als er geen eigen kleur is. */
const DEFAULT_ACCENT = "#A93BF5";
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");

/** Logo ongewijzigd uploaden (geen hercompressie: transparantie van een PNG blijft behouden). */
async function uploadLogo(file: File): Promise<string> {
  const id = crypto.randomUUID();
  const p = await api<PresignResponse>("/files/presign", {
    method: "POST",
    body: { id, kind: "photo", mime: file.type, size: file.size, name: file.name },
  });
  await putToStorage(p, file);
  await api(`/files/${id}/confirm`, { method: "POST", body: {} });
  return id;
}

/**
 * Eigen huisstijl (docs/BRAND.md): logo (in de app en de PDF-rapporten) en accentkleur, in te stellen door de
 * eigenaar of beheerder. Zonder eigen instellingen geldt de rapportstijl uit Platformbeheer.
 */
export function ReportStyleSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["report-style"], queryFn: () => api<ReportStyleDto>("/org/report-style") });
  const [accent, setAccent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!q.data) return null;
  const current = accent ?? q.data.accent ?? DEFAULT_ACCENT;
  const ratio = /^#[0-9a-fA-F]{6}$/.test(current) ? contrastRatio(current, "#FFFFFF") : 0;
  const tooLight = ratio < REPORT_ACCENT_MIN_CONTRAST;

  async function save(body: ReportStyleDto, ok: string) {
    setBusy(true);
    try {
      const r = await api<ReportStyleDto>("/org/report-style", { method: "PUT", body });
      qc.setQueryData(["report-style"], r);
      setAccent(null);
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast(ok);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function onImage(file: File | undefined, kind: "logo" | "icon") {
    if (!file) return;
    if (!(REPORT_LOGO_MIMES as readonly string[]).includes(file.type)) return toast("Kies een PNG of JPEG");
    if (file.size > MAX_LOGO_BYTES)
      return toast(`${kind === "logo" ? "Het logo" : "Het pictogram"} is te groot (max 2 MB)`);
    setBusy(true);
    try {
      const id = await uploadLogo(file);
      setBusy(false);
      await save(
        kind === "logo"
          ? { accent: q.data!.accent, logoFileId: id }
          : { accent: q.data!.accent, logoFileId: q.data!.logoFileId, iconFileId: id },
        kind === "logo" ? "Logo opgeslagen" : "Pictogram opgeslagen",
      );
    } catch (e) {
      setBusy(false);
      toast(errMsg(e));
    }
  }

  return (
    <div className="stack reportstyle" aria-label="Logo en huisstijl" role="group">
      <h3>Logo en huisstijl</h3>
      <p className="hint">
        Het logo staat in de app linksboven (in plaats van het Klantpunt-logo) en in de kop van alle rapporten
        (bevindingen, MJOP, conditiemeting, meterstanden). Met een eigen logo en kleur dragen de rapporten jullie
        huisstijl; onderaan staat dan klein &ldquo;gemaakt met klantpunt.io&rdquo;.
      </p>
      <div className="profile-row">
        <span className="logo-preview" aria-label="Logo">
          {q.data.logoFileId ? (
            <img src={photoUrl(q.data.logoFileId)} alt="Logo van de organisatie" />
          ) : (
            <span className="muted small">Klantpunt-logo</span>
          )}
        </span>
        {canEdit && (
          <span className="btnrow">
            <label className="btn">
              <IconUpload size={18} aria-hidden="true" />
              {q.data.logoFileId ? "Ander logo" : "Logo uploaden"}
              <input
                type="file"
                accept="image/png,image/jpeg"
                hidden
                aria-label="Logo uploaden (PNG of JPEG)"
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  void onImage(f, "logo");
                }}
              />
            </label>
            {q.data.logoFileId && (
              <button
                type="button"
                className="btn ghost"
                disabled={busy}
                onClick={() => void save({ accent: q.data!.accent, logoFileId: null }, "Logo verwijderd")}
              >
                <IconTrash size={18} aria-hidden="true" />
                Logo verwijderen
              </button>
            )}
          </span>
        )}
      </div>
      <p className="hint">
        PNG (bij voorkeur met transparante achtergrond) of JPEG, max 2 MB. Op het donkere voorblad staat het logo op een
        wit vlak.
      </p>
      <div className="profile-row">
        <span className="logo-preview icon-preview" aria-label="Pictogram">
          {q.data.iconFileId ? (
            <img src={photoUrl(q.data.iconFileId)} alt="Pictogram van de organisatie" />
          ) : (
            <span className="muted small">Geen pictogram</span>
          )}
        </span>
        {canEdit && (
          <span className="btnrow">
            <label className="btn">
              <IconUpload size={18} aria-hidden="true" />
              {q.data.iconFileId ? "Ander pictogram" : "Pictogram uploaden"}
              <input
                type="file"
                accept="image/png,image/jpeg"
                hidden
                aria-label="Pictogram uploaden (PNG of JPEG)"
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  void onImage(f, "icon");
                }}
              />
            </label>
            {q.data.iconFileId && (
              <button
                type="button"
                className="btn ghost"
                disabled={busy}
                onClick={() =>
                  void save(
                    { accent: q.data!.accent, logoFileId: q.data!.logoFileId, iconFileId: null },
                    "Pictogram verwijderd",
                  )
                }
              >
                <IconTrash size={18} aria-hidden="true" />
                Pictogram verwijderen
              </button>
            )}
          </span>
        )}
      </div>
      <p className="hint">
        Pictogram (optioneel): vierkant beeldmerk, bijvoorbeeld 256 × 256 px, voor kleine plekken in de app: het
        ingeklapte menu en de bovenbalk op de telefoon. Zonder pictogram staat daar het logo.
      </p>
      <div className="profile-row">
        <label className="field inline">
          <input
            type="color"
            value={/^#[0-9a-fA-F]{6}$/.test(current) ? current : DEFAULT_ACCENT}
            disabled={!canEdit || busy}
            onChange={(e) => setAccent(e.target.value.toUpperCase())}
            aria-label="Accentkleur kiezen"
          />
          <input
            className="hexinput"
            value={current}
            disabled={!canEdit || busy}
            onChange={(e) => setAccent(e.target.value.trim())}
            aria-label="Accentkleur (hex)"
            maxLength={7}
          />
        </label>
        <span className="grow small">
          {tooLight ? (
            <span className="warn">
              Te licht voor tekst en lijnen op wit (contrast{" "}
              {ratio.toLocaleString("nl-NL", { maximumFractionDigits: 1 })}:1, minimaal {REPORT_ACCENT_MIN_CONTRAST}:1)
            </span>
          ) : (
            <span className="muted">
              Contrast op wit {ratio.toLocaleString("nl-NL", { maximumFractionDigits: 1 })}:1
              {q.data.accent ? "" : " · standaardkleur"}
            </span>
          )}
        </span>
      </div>
      {canEdit && (
        <div className="btnrow">
          <button
            type="button"
            className="btn primary"
            disabled={busy || tooLight || accent == null || accent === q.data.accent}
            onClick={() => void save({ accent, logoFileId: q.data!.logoFileId }, "Accentkleur opgeslagen")}
          >
            Kleur opslaan
          </button>
          {(q.data.accent || q.data.logoFileId || q.data.iconFileId) && (
            <button
              type="button"
              className="btn ghost"
              disabled={busy}
              onClick={() =>
                void save({ accent: null, logoFileId: null, iconFileId: null }, "Standaard huisstijl hersteld")
              }
            >
              Standaard herstellen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
